/** Shared country picker — searchable dropdown with flags.
 *
 * Controlled: pass `value` (ISO 3166-1 alpha-2, e.g. "FR") and `onChange`.
 * Names are localised to the active UI language via Intl.DisplayNames;
 * flags are emoji derived from the code. Priority markets (FR first) are
 * pinned to the top; the rest are sorted by localised name. Used
 * everywhere a country is collected — signup, org profile, addresses, …
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  COUNTRY_CODES,
  PRIORITY_COUNTRIES,
  countryName,
  flagEmoji,
} from "@/lib/countries";

type Props = {
  value: string;
  onChange: (code: string) => void;
  id?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Show a "—" entry that clears the value (for optional fields). */
  allowEmpty?: boolean;
  "aria-label"?: string;
};

export function CountrySelector({
  value,
  onChange,
  id,
  disabled,
  className,
  placeholder,
  allowEmpty,
  "aria-label": ariaLabel,
}: Props) {
  const { i18n, t } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const names = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of COUNTRY_CODES) map[c] = countryName(c, locale);
    return map;
  }, [locale]);

  const ordered = useMemo(() => {
    const priority = PRIORITY_COUNTRIES.filter((c) => COUNTRY_CODES.includes(c));
    const rest = COUNTRY_CODES.filter((c) => !priority.includes(c)).sort(
      (a, b) => (names[a] || a).localeCompare(names[b] || b, locale),
    );
    return [...priority, ...rest];
  }, [names, locale]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter(
      (c) =>
        c.toLowerCase().includes(q) ||
        (names[c] || "").toLowerCase().includes(q),
    );
  }, [ordered, query, names]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (code: string) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className="input flex w-full items-center justify-between gap-2 text-left disabled:opacity-50"
        onClick={() => setOpen((o) => !o)}
      >
        {value ? (
          <span className="flex items-center gap-2">
            <span aria-hidden>{flagEmoji(value)}</span>
            <span>{names[value] ?? value}</span>
          </span>
        ) : (
          <span className="text-nb-text-3">
            {placeholder ?? t("country.select", "Select a country")}
          </span>
        )}
        <span aria-hidden className="text-nb-text-3">
          ▾
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-nb-border bg-nb-surface shadow-lg">
          <div className="border-b border-nb-border p-2">
            <input
              autoFocus
              className="input w-full"
              placeholder={t("country.search", "Search…")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-auto py-1">
            {allowEmpty && (
              <li>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-nb-text-3 hover:bg-nb-surface-2"
                  onClick={() => pick("")}
                >
                  —
                </button>
              </li>
            )}
            {filtered.map((c) => (
              <li key={c}>
                <button
                  type="button"
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm text-nb-text hover:bg-nb-surface-2 ${
                    c === value ? "bg-nb-surface-2" : ""
                  }`}
                  onClick={() => pick(c)}
                >
                  <span aria-hidden>{flagEmoji(c)}</span>
                  <span className="flex-1 text-left">{names[c] ?? c}</span>
                  <span className="text-xs text-nb-text-3">{c}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-nb-text-3">
                {t("country.noResults", "No match")}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
